/* La carte des revendeurs — voir sections/simply-revendeurs.liquid
 *
 * Vanilla, sans dependance, conformement a la regle du projet. Un seul element
 * personnalise. MapLibre n'est pas une exception a cette regle : ce n'est pas un
 * framework front, c'est une bibliotheque de carte, versee dans assets/ et
 * chargee a la demande par ce fichier — jamais au chargement de la page.
 *
 * Principe, le meme que le selecteur de montage : la liste est rendue par Liquid
 * et fonctionne sans JavaScript. Ce script ne construit aucune fiche, il lit
 * celles qui sont deja la, les reordonne, les masque, et pose un marqueur par
 * ligne. Une seule source de balisage.
 *
 * Ce qu'il ne fait pas : demander la position du visiteur. « Near me » est un
 * bouton, et il n'y a pas d'autre chemin vers `geolocation`.
 */

const SEUIL_LARGE = 900;
// Vue de depart de la carte : le monde entier (demande de Yoann, 10/10), depuis
// que des magasins existent au Japon et en Amerique du Nord. Bornes et non zoom
// fixe : la carte s'ajuste a la largeur de son cadre, du telephone au bureau.
// Ni poles ni Antarctique, qui n'ont pas de magasin et que Mercator etire.
const MONDE = [[-170, -56], [180, 75]];

class SimplyRevendeurs extends HTMLElement {
  connectedCallback() {
    this.liste = this.querySelector('[data-liste]');
    if (!this.liste) return;

    this.fiches = [...this.liste.querySelectorAll('.simply-revendeurs__item')].map((el) => ({
      el,
      nom: el.dataset.nom || '',
      adresse: el.dataset.adresse || '',
      npa: el.dataset.npa || '',
      ville: el.dataset.ville || '',
      region: el.dataset.region || '',
      pays: el.dataset.pays || '',
      essai: el.dataset.essai === '1',
      lat: parseFloat(el.dataset.lat),
      lon: parseFloat(el.dataset.lon),
      marqueur: null,
    }));
    if (!this.fiches.length) return;

    this.zoneCompte = this.querySelector('[data-compte]');
    this.zoneVide = this.querySelector('[data-vide]');
    this.panneauCarte = this.querySelector('[data-panneau="carte"]');
    this.conteneurCarte = this.querySelector('[data-carte]');
    this.boutonGeo = this.querySelector('[data-geo]');

    this.filtrePays = '';
    this.filtreType = 'tous';
    this.recherche = '';
    this.actif = null;
    this.carte = null;
    this.chargement = null;

    this.ordonne();
    this.branche();
    this.rafraichit();

    // Les commandes ne servaient a rien tant que ce script n'etait pas la :
    // c'est maintenant qu'elles apparaissent.
    this.reveille('[data-filtres]');
    this.reveille('[data-onglets]');

    this.vue('liste');
    this.surLargeur();
    window.addEventListener('resize', () => this.surLargeur());
  }

  reveille(selecteur) {
    const el = this.querySelector(selecteur);
    if (el) el.hidden = false;
  }

  /* ------------------------------------------------------------------ ordre */

  /* Pays dans l'ordre des boutons (celui du §12), puis nom. Liquid rend les
   * fiches dans l'ordre des metaobjects, qui n'a pas de sens pour un lecteur ;
   * `order` reordonne sans toucher au DOM, donc sans rien reconstruire. */
  ordonne() {
    const pays = [...this.querySelectorAll('button[data-pays]')].map((b) => b.dataset.pays);
    const rangPays = (f) => (pays.includes(f.pays) ? pays.indexOf(f.pays) : pays.length);
    const rang = [...this.fiches].sort(
      (a, b) => rangPays(a) - rangPays(b) || a.nom.localeCompare(b.nom, 'fr')
    );
    rang.forEach((f, i) => {
      f.el.style.order = i;
    });
  }

  /* ---------------------------------------------------------------- filtres */

  /* Le centre de test seul, ou tous les magasins : deux boutons exclusifs. */
  get testSeul() {
    return this.filtreType === 'test';
  }

  branche() {
    // Les <li> portent aussi data-pays : seuls les boutons nous interessent.
    this.querySelectorAll('button[data-pays]').forEach((bouton) => {
      bouton.addEventListener('click', () => {
        // Un pays deja choisi se relache : le doc n'a pas de bouton « tous les pays ».
        this.filtrePays = this.filtrePays === bouton.dataset.pays ? '' : bouton.dataset.pays;
        this.querySelectorAll('button[data-pays]').forEach((b) =>
          b.setAttribute('aria-pressed', String(b.dataset.pays === this.filtrePays))
        );
        this.rafraichit();
        this.cadrePays();
      });
    });

    this.querySelectorAll('button[data-type]').forEach((bouton) => {
      bouton.addEventListener('click', () => {
        this.filtreType = bouton.dataset.type;
        this.querySelectorAll('button[data-type]').forEach((b) =>
          b.setAttribute('aria-pressed', String(b === bouton))
        );
        this.rafraichit();
      });
    });

    const champ = this.querySelector('[data-recherche]');
    if (champ) {
      champ.addEventListener('input', () => {
        this.recherche = champ.value.trim().toLowerCase();
        this.rafraichit();
      });
    }

    this.querySelectorAll('[data-vue]').forEach((onglet) => {
      onglet.addEventListener('click', () => this.vue(onglet.dataset.vue));
    });

    this.fiches.forEach((f, i) => {
      f.el.addEventListener('click', (e) => {
        // Un clic sur « Website » ou sur un numero doit partir, pas selectionner.
        if (e.target.closest('a')) return;
        this.choisit(i, 'liste');
      });
    });

    if (this.boutonGeo) this.boutonGeo.addEventListener('click', () => this.autourDeMoi());
  }

  retenues() {
    return this.fiches.filter(
      (f) =>
        (!this.filtrePays || f.pays === this.filtrePays) &&
        (!this.testSeul || f.essai) &&
        (!this.recherche ||
          `${f.nom} ${f.adresse} ${f.npa} ${f.ville} ${f.region}`.toLowerCase().includes(this.recherche))
    );
  }

  rafraichit() {
    const gardees = new Set(this.retenues());
    this.fiches.forEach((f) => {
      const visible = gardees.has(f);
      f.el.hidden = !visible;
      if (f.marqueur) f.marqueur.getElement().hidden = !visible;
    });

    if (this.zoneVide) this.zoneVide.hidden = gardees.size > 0;
    this.compte(gardees.size);
  }

  /* Un pays choisi cadre la carte sur ses magasins : la vue de depart montre le
   * monde, ou un pays tient en quelques pixels. Relache, le filtre ramene la vue
   * de depart. La recherche et le type ne recadrent pas : ils affinent
   * ce qu'on regarde deja. */
  cadrePays() {
    if (!this.carte) return;
    const points = this.retenues().filter((f) => !Number.isNaN(f.lat) && !Number.isNaN(f.lon));
    if (!this.filtrePays || points.length === 0) {
      this.carte.fitBounds(MONDE, { padding: 0, essential: false });
      return;
    }
    const bornes = new maplibregl.LngLatBounds();
    points.forEach((f) => bornes.extend([f.lon, f.lat]));
    this.carte.fitBounds(bornes, { padding: 60, maxZoom: 11, essential: false });
  }

  /* Le libelle vient des traductions, pose par Liquid en attribut avec `%n%`
   * comme trou. Aucun texte visible n'est ecrit dans ce fichier. */
  compte(n) {
    if (!this.zoneCompte) return;
    const d = this.zoneCompte.dataset;
    const modele = this.testSeul
      ? n === 1
        ? d.testUn
        : d.testN
      : n === 1
        ? d.un
        : d.n;
    if (modele) this.zoneCompte.textContent = modele.replace('%n%', n);
  }

  /* ------------------------------------------------------------------- vues */

  vue(nom) {
    this.dataset.vue = nom;
    this.querySelectorAll('[data-vue]').forEach((o) =>
      o.setAttribute('aria-selected', String(o.dataset.vue === nom))
    );
    if (nom === 'carte') this.monteLaCarte();
  }

  /* En vue large, les deux panneaux sont visibles : la carte doit exister des le
   * depart. En vue etroite, elle n'est montee que si le visiteur ouvre l'onglet
   * — c'est la que se gagnent les 200 ko de MapLibre sur un telephone en 4G. */
  surLargeur() {
    const large = window.innerWidth >= SEUIL_LARGE;
    if (large === this.etaitLarge) return;
    this.etaitLarge = large;
    if (large) {
      this.dataset.vue = 'liste';
      this.monteLaCarte();
    }
  }

  /* ------------------------------------------------------------------ carte */

  /* Charge la bibliotheque une seule fois. Si elle ne vient pas — reseau coupe,
   * fichier absent — la page garde sa liste et le panneau reste ferme : on ne
   * laisse jamais un rectangle vide a la place d'une carte. */
  monteLaCarte() {
    if (this.carte || this.chargement) return this.chargement;
    this.chargement = this.chargeMapLibre()
      .then(() => this.dessine())
      .catch(() => {
        // Ni colonne vide en vue large, ni onglet qui mene nulle part en vue
        // etroite : la page redevient exactement ce qu'elle est sans script,
        // une liste complete.
        this.panneauCarte.hidden = true;
        this.classList.add('simply-revendeurs--sans-carte');
        const onglets = this.querySelector('[data-onglets]');
        if (onglets) onglets.hidden = true;
        this.dataset.vue = 'liste';
      });
    return this.chargement;
  }

  chargeMapLibre() {
    if (window.maplibregl) return Promise.resolve();
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = this.dataset.carteCss;
    document.head.appendChild(css);

    return new Promise((ok, non) => {
      const js = document.createElement('script');
      js.src = this.dataset.carteJs;
      js.onload = ok;
      js.onerror = non;
      document.head.appendChild(js);
    });
  }

  dessine() {
    this.panneauCarte.hidden = false;

    this.carte = new maplibregl.Map({
      container: this.conteneurCarte,
      style: this.dataset.carteStyle,
      bounds: MONDE,
      // L'attribution OpenStreetMap n'est pas decorative : la licence ODbL
      // l'exige. Compacte, jamais supprimee.
      attributionControl: { compact: true },
    });
    this.carte.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

    this.fiches.forEach((f, i) => {
      if (Number.isNaN(f.lat) || Number.isNaN(f.lon)) return;
      const point = document.createElement('div');
      point.className =
        'simply-revendeurs__marqueur' +
        (f.essai ? '' : ' simply-revendeurs__marqueur--sans-essai');
      point.title = f.nom;
      point.addEventListener('click', (e) => {
        e.stopPropagation();
        this.choisit(i, 'carte');
      });
      // En vue etroite, la liste n'est pas a l'ecran quand la carte l'est : sans
      // bulle, un marqueur clique ne dirait pas de quel magasin il s'agit, ni
      // comment s'y rendre. Le contenu est repris du DOM, aucun texte n'est
      // ecrit ici.
      f.bulle = new maplibregl.Popup({ offset: 16, closeButton: false })
        .setLngLat([f.lon, f.lat])
        .setDOMContent(this.contenuBulle(f));
      f.marqueur = new maplibregl.Marker({ element: point })
        .setLngLat([f.lon, f.lat])
        .addTo(this.carte);
    });

    if (this.boutonGeo) this.boutonGeo.hidden = false;
    this.rafraichit();
  }

  /* Nom, adresse, itineraire — les deux derniers clones depuis la fiche. Rien
   * n'est duplique a la main : le jour ou une adresse change dans le metaobject,
   * la bulle change avec elle. */
  contenuBulle(f) {
    const boite = document.createElement('div');
    boite.className = 'simply-revendeurs__bulle';

    const nom = document.createElement('p');
    nom.className = 'simply-revendeurs__bulle-nom';
    nom.textContent = f.nom;
    boite.append(nom);

    const adresse = f.el.querySelector('.simply-revendeurs__adresse');
    if (adresse) boite.append(adresse.cloneNode(true));

    const itineraire = f.el.querySelector('[data-itineraire]');
    if (itineraire) boite.append(itineraire.cloneNode(true));

    return boite;
  }

  /* ---------------------------------------------------------------- choisir */

  choisit(i, origine) {
    const f = this.fiches[i];
    this.actif = i;

    this.fiches.forEach((autre, j) => {
      autre.el.toggleAttribute('data-actif', j === i);
      if (autre.marqueur) autre.marqueur.getElement().toggleAttribute('data-actif', j === i);
    });

    // `essential` reste a faux : le vol plane est un confort, et un visiteur qui
    // a demande moins d'animations a son systeme ne l'a pas demande.
    const centre = () => {
      if (!this.carte || Number.isNaN(f.lat)) return;
      this.carte.flyTo({ center: [f.lon, f.lat], zoom: 11, speed: 1.2, essential: false });
      if (this.bulleOuverte && this.bulleOuverte !== f.bulle) this.bulleOuverte.remove();
      if (f.bulle) {
        f.bulle.addTo(this.carte);
        this.bulleOuverte = f.bulle;
      }
    };

    if (origine === 'carte') {
      f.el.scrollIntoView({ block: 'nearest', behavior: this.douceur() });
      centre();
      return;
    }

    if (window.innerWidth < SEUIL_LARGE) {
      // En vue etroite la carte n'est pas a l'ecran : choisir une fiche l'y
      // amene. Le recentrage attend que la bibliotheque soit la, sans quoi le
      // premier clic ouvrirait une carte centree sur rien.
      Promise.resolve(this.monteLaCarte()).then(() => {
        this.vue('carte');
        centre();
      });
      return;
    }

    centre();
  }

  douceur() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /* Jamais appele ailleurs que depuis le bouton. Un refus n'affiche pas
   * d'erreur : le visiteur vient de dire non, le lui repeter serait grossier. */
  autourDeMoi() {
    if (!navigator.geolocation || !this.carte) return;
    navigator.geolocation.getCurrentPosition((p) => {
      this.carte.flyTo({ center: [p.coords.longitude, p.coords.latitude], zoom: 9 });
    });
  }
}

if (!customElements.get('simply-revendeurs')) {
  customElements.define('simply-revendeurs', SimplyRevendeurs);
}
